import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { GoogleMap, Marker as GoogleMarker, useLoadScript } from '@react-google-maps/api';
import {
  IoShieldCheckmark, IoVideocam, IoKeypad, IoCarSport, IoCube, IoHome,
  IoWater, IoFlash, IoBuild, IoArrowUp, IoCar,
  IoFitness, IoGameController, IoWine, IoFilm, IoLeaf, IoBody,
  IoTennisball, IoBaseball, IoFootball, IoBasketball, IoFlower, IoWalk,
  IoIceCream, IoBicycle, IoStorefront, IoRestaurant, IoMedical, IoPulse,
  IoCart, IoCard, IoShirt, IoWifi, IoPaw, IoSunny, IoBulb,
  IoTrash, IoPeople, IoBriefcase, IoLocationSharp,
} from 'react-icons/io5';
import { MdSensors } from 'react-icons/md';
import LeftNav from '../common/LeftNav';
import TopNav from '../common/TopNav';
import adminCrmService from '../../../services/adminCrmService';
import propertyService from '../../../services/propertyService';
import arrowleftt from '../../assets/img/arrowleftt.svg';
import { toast } from 'react-toastify';

const MAPS_LIBRARIES = ['places'];

// ─── Constants ────────────────────────────────────────────────────────────────
const projectTypeOptions = ['Apartments', 'Villas', 'Commercial', 'Plots', 'Farm Lands'];
// Farm Lands behaves exactly like Plots: SQYD unit, no BHK, same community type options
const isPlotLikeType = (type) => type === 'Plots' || type === 'Farm Lands';
const projectStatusOptions = ['Under Construction', 'Ready to Move', 'Upcoming', 'Completed'];
const facingOptions = ['East', 'West', 'North', 'South', 'North-East', 'North-West', 'South-East', 'South-West'];
const bhkTypeOptions = ['1 BHK', '1.5 BHK', '2 BHK', '2.5 BHK', '3 BHK', '3.5 BHK', '4 BHK', '4.5 BHK', '5 BHK', '5+ BHK'];

const propertyTypeMap = {
  'Apartments': { type: 'residential', subtype: 'apartment' },
  'Villas': { type: 'residential', subtype: 'villa' },
  'Commercial': { type: 'commercial', subtype: null },
  'Plots': { type: 'plot', subtype: null },
  'Farm Lands': { type: 'farmland', subtype: null },
};

const amenityTabs = ['Security', 'Basic Infrastructure', 'Recreational Amenities', 'Convenience Amenities', 'Other Amenities'];
const amenitiesData = {
  'Security': [
    { id: 1, name: '24/7 security personnel', Icon: IoShieldCheckmark },
    { id: 2, name: 'CCTV', Icon: IoVideocam },
    { id: 3, name: 'Access control systems', Icon: IoKeypad },
    { id: 4, name: 'Under car scanner', Icon: IoCarSport },
    { id: 5, name: 'Smart delivery boxes', Icon: IoCube },
    { id: 6, name: 'Basement security system', Icon: IoHome },
  ],
  'Basic Infrastructure': [
    { id: 7, name: 'Water supply', Icon: IoWater },
    { id: 8, name: 'Power backup', Icon: IoFlash },
    { id: 9, name: 'Sewage management', Icon: IoBuild },
    { id: 10, name: 'Elevator', Icon: IoArrowUp },
    { id: 11, name: 'Ample parking', Icon: IoCar },
  ],
  'Recreational Amenities': [
    { id: 12, name: 'Swimming Pool', Icon: IoWater },
    { id: 13, name: 'GYM', Icon: IoFitness },
    { id: 14, name: 'Kids Play', Icon: IoGameController },
    { id: 15, name: 'Club house', Icon: IoHome },
    { id: 16, name: 'Party room', Icon: IoWine },
    { id: 17, name: 'Theatre', Icon: IoFilm },
    { id: 18, name: 'Garden', Icon: IoLeaf },
    { id: 19, name: 'Party area', Icon: IoWine },
    { id: 20, name: 'Yoga Center', Icon: IoBody },
    { id: 21, name: 'Shuttle court', Icon: IoTennisball },
    { id: 22, name: 'Cricket practice net', Icon: IoBaseball },
    { id: 23, name: 'Football court', Icon: IoFootball },
    { id: 24, name: 'Basketball Court', Icon: IoBasketball },
    { id: 25, name: 'Meditation area', Icon: IoFlower },
    { id: 26, name: 'Jogging track', Icon: IoWalk },
    { id: 27, name: 'Skating rink', Icon: IoIceCream },
    { id: 28, name: 'Cycling Path', Icon: IoBicycle },
  ],
  'Convenience Amenities': [
    { id: 29, name: 'On-site retail spaces', Icon: IoStorefront },
    { id: 30, name: 'On-site restaurant', Icon: IoRestaurant },
    { id: 31, name: 'Medical store', Icon: IoMedical },
    { id: 32, name: 'Diagnostic center', Icon: IoPulse },
    { id: 33, name: 'Grocery store', Icon: IoCart },
    { id: 34, name: 'ATM', Icon: IoCard },
    { id: 35, name: 'Laundry facilities', Icon: IoShirt },
    { id: 36, name: 'High speed internet', Icon: IoWifi },
  ],
  'Other Amenities': [
    { id: 37, name: 'Pet friendly amenities', Icon: IoPaw },
    { id: 38, name: 'Car wash', Icon: IoCarSport },
    { id: 39, name: 'Solar for 24hr hot water', Icon: IoSunny },
    { id: 40, name: 'LED lobby lights', Icon: IoBulb },
    { id: 41, name: 'Balcony garden', Icon: IoFlower },
    { id: 42, name: 'Mini theater', Icon: IoFilm },
    { id: 43, name: 'Resident lounge', Icon: IoPeople },
    { id: 44, name: 'Common area WiFi', Icon: IoWifi },
    { id: 45, name: 'Valet Trash service', Icon: IoTrash },
    { id: 46, name: 'on-site management', Icon: IoBriefcase },
  ],
};

// ─── Indian currency words helper ─────────────────────────────────────────────
const numberToIndianWords = (num) => {
  if (!num || isNaN(num)) return '';
  const n = Math.round(parseFloat(String(num).replace(/,/g, '')));
  if (n === 0) return '';
  const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
    'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
  const twoDigit = (n) => n < 20 ? ones[n] : tens[Math.floor(n / 10)] + (n % 10 ? ' ' + ones[n % 10] : '');
  const threeDigit = (n) => n >= 100 ? ones[Math.floor(n / 100)] + ' Hundred' + (n % 100 ? ' ' + twoDigit(n % 100) : '') : twoDigit(n);
  if (n < 1000) return threeDigit(n);
  let result = '';
  const crores = Math.floor(n / 10000000);
  const lakhs = Math.floor((n % 10000000) / 100000);
  const thousands = Math.floor((n % 100000) / 1000);
  const remainder = n % 1000;
  if (crores > 0) result += threeDigit(crores) + ' Crore' + (crores > 1 ? 's' : '');
  if (lakhs > 0) result += (result ? ' ' : '') + twoDigit(lakhs) + ' Lakh' + (lakhs > 1 ? 's' : '');
  if (thousands > 0) result += (result ? ' ' : '') + twoDigit(thousands) + ' Thousand';
  if (remainder > 0) result += (result ? ' ' : '') + threeDigit(remainder);
  return '₹ ' + result;
};

const getFullUrl = (url) => url && !url.startsWith('http') ? `https://sandbox.rebax.in${url}` : url;

// ─── Styles ───────────────────────────────────────────────────────────────────
const S = {
  section: { backgroundColor: '#fff', borderRadius: 12, padding: 16, marginBottom: 16, border: '1px solid #F3F4F6' },
  sectionTitle: { fontSize: 16, fontWeight: 700, color: '#111827', marginBottom: 12, paddingBottom: 8, borderBottom: '2px solid #E5E7EB' },
  label: { fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6, display: 'block' },
  input: { width: '100%', padding: '10px 14px', borderRadius: 10, border: '1px solid #E5E7EB', fontSize: 14, color: '#111827', backgroundColor: '#F9FAFB', outline: 'none', boxSizing: 'border-box' },
  select: { width: '100%', padding: '10px 14px', borderRadius: 10, border: '1px solid #E5E7EB', fontSize: 14, color: '#111827', backgroundColor: '#F9FAFB', outline: 'none', boxSizing: 'border-box', cursor: 'pointer' },
  hint: { fontSize: 11, color: '#2563EB', marginTop: 4, fontStyle: 'italic' },
  fieldWrap: { marginBottom: 14 },
  unitRow: { display: 'flex', alignItems: 'stretch', border: '1px solid #E5E7EB', borderRadius: 10, overflow: 'hidden', backgroundColor: '#F9FAFB' },
  unitInput: { flex: 1, padding: '10px 14px', border: 'none', fontSize: 14, color: '#111827', backgroundColor: 'transparent', outline: 'none' },
  unitBtn: { padding: '10px 14px', backgroundColor: '#2563EB', color: '#fff', border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' },
  chip: { padding: '7px 14px', borderRadius: 20, border: '1px solid #E5E7EB', backgroundColor: '#F9FAFB', cursor: 'pointer', fontSize: 12, color: '#6B7280', marginRight: 8, marginBottom: 8 },
  chipActive: { padding: '7px 14px', borderRadius: 20, border: '1px solid #2563EB', backgroundColor: '#EFF6FF', cursor: 'pointer', fontSize: 12, color: '#2563EB', fontWeight: 600, marginRight: 8, marginBottom: 8 },
  uploadBox: { border: '1px dashed #D1D5DB', borderRadius: 10, padding: '20px', textAlign: 'center', cursor: 'pointer', backgroundColor: '#F9FAFB' },
  docCard: { display: 'flex', alignItems: 'center', gap: 10, backgroundColor: '#EFF6FF', borderRadius: 8, padding: 10, border: '1px solid #BFDBFE' },
  floorCard: { backgroundColor: '#F9FAFB', borderRadius: 8, padding: 12, marginBottom: 8, border: '1px solid #E5E7EB' },
  addFloorBox: { backgroundColor: '#F9FAFB', borderRadius: 10, padding: 14, border: '1px solid #E5E7EB', marginTop: 8 },
  amenityItem: { display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '10px 8px', borderRadius: 10, border: '1px solid #E5E7EB', backgroundColor: '#F9FAFB', cursor: 'pointer', fontSize: 11, color: '#6B7280', textAlign: 'center', minWidth: 80 },
  amenityItemActive: { display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '10px 8px', borderRadius: 10, border: '1px solid #2563EB', backgroundColor: '#EFF6FF', cursor: 'pointer', fontSize: 11, color: '#2563EB', fontWeight: 600, textAlign: 'center', minWidth: 80 },
  uspCard: { backgroundColor: '#F9FAFB', borderRadius: 8, padding: 12, marginTop: 8, border: '1px solid #E5E7EB' },
};

// ─── Shared field components (defined outside to prevent remount on each keystroke) ──
const Field = ({ label, value, onChange, placeholder, type = 'text', required, showWords }) => (
  <div style={S.fieldWrap}>
    <label style={S.label}>{label}{required && <span style={{ color: '#EF4444' }}> *</span>}</label>
    <input
      style={S.input}
      type={type}
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
    />
    {showWords && value && !isNaN(parseFloat(value)) && parseFloat(value) > 0 && (
      <div style={S.hint}>{numberToIndianWords(value)}</div>
    )}
  </div>
);

const UnitField = ({ label, value, onChange, unit, onToggleUnit, placeholder, required, showWords, perLabel }) => (
  <div style={S.fieldWrap}>
    {label && <label style={S.label}>{label}{required && <span style={{ color: '#EF4444' }}> *</span>}</label>}
    <div style={S.unitRow}>
      <input
        style={S.unitInput}
        type="number"
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder || '0'}
      />
      {perLabel && <span style={{ padding: '0 8px', color: '#9CA3AF', fontSize: 12, alignSelf: 'center' }}>per</span>}
      <button style={S.unitBtn} type="button" onClick={onToggleUnit}>{unit} ▾</button>
    </div>
    {showWords && value && !isNaN(parseFloat(value)) && parseFloat(value) > 0 && (
      <div style={S.hint}>{numberToIndianWords(value)}</div>
    )}
  </div>
);

export default function AdminCrmAddProject() {
  const navigate = useNavigate();
  const location = useLocation();
  const { userId, userName, userRole, userEmail, userPhone, project: initialProject, mode } = location.state || {};
  const isEditMode = mode === 'edit' && !!initialProject;

  // ─── File input refs ──────────────────────────────────────────────────────
  const thumbnailRef = useRef(null);
  const galleryRef = useRef(null);
  const floorPlanImgRef = useRef(null);
  const floorPlanFormRef = useRef(null);
  const paymentPlanRef = useRef(null);
  const termsRef = useRef(null);

  // ─── Form State ───────────────────────────────────────────────────────────
  const [formData, setFormData] = useState({
    projectName: '', agentCommission: '', payoutAfter: '',
    projectOverview: '',
    usps: [{ title: '', content: '' }],
    projectType: '', projectCommunity: '', projectStatus: '',
    possessionDate: '', completionDate: '',
    projectArea: '', pricePerSqFt: '', priceUnit: 'SFT',
    noOfUnits: '', noOfFloors: '', noOfTowers: '', reraId: '',
    budget: { min: '', max: '' },
    amenitiesCost: '', carParkingCost: '', extraCarParkingCost: '',
    facing: [], facingCharges: {},
    cornerPremium: '', cornerPremiumUnit: 'SFT',
    maintenanceCost: '', maintenanceUnit: 'SFT',
    floorRiseCharges: '', floorRiseUnit: 'SFT', floorRiseFromFloor: '',
    corpusFund: '', corpusFundUnit: 'SFT',
    floorPlans: [],
    currentFloorPlan: { title: '', size: '', sizeUnit: 'SFT', type: '', floors: '', facing: '' },
    currentFloorPlanImage: null,
    editingFloorPlanId: null,
    selectedAmenities: [],
    location: '', latitude: 17.4481, longitude: 78.3908,
    addressLocality: '', addressCity: '', addressState: '', addressPincode: '', areaName: '',
    gallery: [],
    thumbnail: null,
    paymentPlans: null,
    termsAndConditions: null,
  });

  // ─── UI States ────────────────────────────────────────────────────────────
  const [isUploading, setIsUploading] = useState(false);
  const [isLoadingProject, setIsLoadingProject] = useState(false);
  const [communityOptions, setCommunityOptions] = useState([]);
  const [communityLoading, setCommunityLoading] = useState(false);
  const [selectedAmenityTab, setSelectedAmenityTab] = useState('Security');

  // Approval modal
  const [approvalModalVisible, setApprovalModalVisible] = useState(false);
  const [savedProjectId, setSavedProjectId] = useState(null);
  const [approvalLoading, setApprovalLoading] = useState(false);

  // Map modal
  const [mapVisible, setMapVisible] = useState(false);
  const [mapCenter, setMapCenter] = useState({ lat: 17.4481, lng: 78.3908 });
  const [markerPos, setMarkerPos] = useState({ lat: 17.4481, lng: 78.3908 });
  const [mapSearch, setMapSearch] = useState('');
  const [mapSearchResults, setMapSearchResults] = useState([]);
  const [isMapSearching, setIsMapSearching] = useState(false);

  useEffect(() => {
    if (isEditMode) return; // Don't restore location in edit mode, as it will be fetched from API
    const savedLocation = localStorage.getItem('projectLocation');

    if (savedLocation) {
      try {
        const locationData = JSON.parse(savedLocation);

        setFormData(prev => ({
          ...prev,
          ...locationData,
        }));

        // Also restore map position
        if (locationData.latitude !== undefined && locationData.longitude !== undefined) {
          const position = {
            lat: Number(locationData.latitude),
            lng: Number(locationData.longitude),
          };

          setMapCenter(position);
          setMarkerPos(position);
        }
      } catch (error) {
        console.error('Failed to restore location', error);
      }
    }
  }, [isEditMode]);

  // Persist the picked location so it survives a refresh or a back-and-forth navigation
  useEffect(() => {
    if (isEditMode || !formData.location) return;
    try {
      localStorage.setItem('projectLocation', JSON.stringify({
        location: formData.location,
        latitude: formData.latitude,
        longitude: formData.longitude,
        areaName: formData.areaName,
        addressLocality: formData.addressLocality,
        addressCity: formData.addressCity,
        addressState: formData.addressState,
        addressPincode: formData.addressPincode,
      }));
    } catch (error) {
      console.error('Failed to save location', error);
    }
  }, [isEditMode, formData.location, formData.latitude, formData.longitude, formData.areaName, formData.addressLocality, formData.addressCity, formData.addressState, formData.addressPincode]);
  const { isLoaded: mapsLoaded } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '',
    libraries: MAPS_LIBRARIES,
  });


  // ─── Fetch community types on projectType change ──────────────────────────
  useEffect(() => {
    if (!formData.projectType) return;
    setCommunityLoading(true);
    propertyService.getCommunityTypes(formData.projectType)
      .then(res => {
        const data = res?.data || [];
        setCommunityOptions(Array.isArray(data) ? data : []);
      })
      .catch(() => setCommunityOptions([]))
      .finally(() => setCommunityLoading(false));
  }, [formData.projectType]);

  // ─── Prefill for edit mode ────────────────────────────────────────────────
  useEffect(() => {
    if (!isEditMode || !initialProject) return;
    setIsLoadingProject(true);
    propertyService.getPropertyById(initialProject.id)
      .then(res => prefillForm(res?.data || initialProject))
      .catch(() => prefillForm(initialProject))
      .finally(() => setIsLoadingProject(false));
  }, [isEditMode, initialProject?.id]); // eslint-disable-line

  const prefillForm = (p) => {
    if (!p) return;
    const reverseType = (type, subtype) => {
      if (type === 'residential' && subtype === 'apartment') return 'Apartments';
      if (type === 'residential' && subtype === 'villa') return 'Villas';
      if (type === 'commercial') return 'Commercial';
      if (type === 'plot') return 'Plots';
      if (type === 'farmland') return 'Farm Lands';
      return '';
    };
    const toDateInput = (ds) => {
      if (!ds) return '';
      try { return new Date(ds).toISOString().slice(0, 10); } catch { return ''; }
    };

    const processedGallery = (p.images || []).map(url => ({ url: getFullUrl(url), isExisting: true }));
    const processedFloorPlans = (p.floor_plans || []).map(plan => ({
      ...plan, id: plan.id || Date.now().toString() + Math.random(),
      imagePreview: plan.image_url ? getFullUrl(plan.image_url) : null,
    }));
    const processedAmenities = (p.amenities || []).map(name => {
      for (const cat of Object.values(amenitiesData)) {
        const a = cat.find(a => a.name === name);
        if (a) return a.id;
      }
      return null;
    }).filter(Boolean);

    setFormData(prev => ({
      ...prev,
      projectName: p.property_title || '',
      agentCommission: p.agent_commission ? String(p.agent_commission) : '',
      payoutAfter: p.payout_after ? String(p.payout_after) : '',
      projectOverview: p.description || '',
      usps: p.usp?.length > 0 ? p.usp.map(u => ({ title: u.title || '', content: u.content || '' }))
        : p.features?.length > 0 ? p.features.map(f => ({ title: f, content: '' }))
          : [{ title: '', content: '' }],
      projectType: reverseType(p.property_type, p.property_subtype),
      projectCommunity: p.community_type || '',
      projectStatus: p.project_status || '',
      completionDate: toDateInput(p.completion_date),
      possessionDate: toDateInput(p.possession_date),
      projectArea: String(p.area_sqft || ''),
      pricePerSqFt: String(p.price_per_sqft || ''),
      noOfUnits: String(p.no_of_units || ''),
      noOfFloors: String(p.total_floors || ''),
      noOfTowers: String(p.no_of_towers || ''),
      reraId: p.rera_id || '',
      budget: { min: String(p.price || ''), max: p.price_max ? String(p.price_max) : '' },
      amenitiesCost: String(p.amenities_cost || ''),
      carParkingCost: String(p.car_parking_cost || ''),
      extraCarParkingCost: String(p.extra_car_parking_cost || ''),
      facing: p.facing ? (Array.isArray(p.facing) ? p.facing : p.facing.split(',').map(f => f.trim())) : [],
      facingCharges: p.facing_charges ? (typeof p.facing_charges === 'string' ? JSON.parse(p.facing_charges) : p.facing_charges) : {},
      cornerPremium: String(p.corner_premium || ''),
      floorRiseCharges: String(p.floor_rise || ''),
      floorRiseFromFloor: String(p.floor_rise_from_floor || ''),
      corpusFund: String(p.corpus_fund || ''),
      maintenanceCost: String(p.maintenance_cost || ''),
      floorPlans: processedFloorPlans,
      selectedAmenities: processedAmenities,
      location: p.address || '',
      latitude: parseFloat(p.latitude) || 17.4481,
      longitude: parseFloat(p.longitude) || 78.3908,
      addressLocality: p.locality || '',
      addressCity: p.city || '',
      addressState: p.state || '',
      addressPincode: p.pincode || '',
      areaName: p.locality || '',
      gallery: processedGallery,
      thumbnail: p.thumbnail ? { url: getFullUrl(p.thumbnail), isExisting: true } : null,
      paymentPlans: p.payment_plan_url ? { url: getFullUrl(p.payment_plan_url), name: 'payment_plan.pdf', isExisting: true } : null,
      termsAndConditions: p.terms_url ? { url: getFullUrl(p.terms_url), name: 'terms.pdf', isExisting: true } : null,
    }));
  };

  const upd = (key, val) => setFormData(prev => ({ ...prev, [key]: val }));

  // ─── File Handlers ────────────────────────────────────────────────────────
  const handleImageFile = (type, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const preview = URL.createObjectURL(file);
    if (type === 'thumbnail') upd('thumbnail', { file, preview, name: file.name });
    else if (type === 'floorPlan') upd('currentFloorPlanImage', { file, preview, name: file.name });
    e.target.value = '';
  };

  const handleGalleryFiles = (e) => {
    const files = Array.from(e.target.files || []);
    const newImgs = files.map(f => ({ file: f, preview: URL.createObjectURL(f), name: f.name }));
    setFormData(prev => ({ ...prev, gallery: [...prev.gallery, ...newImgs] }));
    e.target.value = '';
  };

  const handleDocFile = (type, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type?.includes('pdf')) { toast.warning('Only PDF files allowed.'); return; }
    const doc = { file, name: file.name };
    if (type === 'payment') upd('paymentPlans', doc);
    else if (type === 'terms') upd('termsAndConditions', doc);
    e.target.value = '';
  };

  // ─── Floor Plans ──────────────────────────────────────────────────────────
  const emptyFloorPlan = (sizeUnit = 'SFT') => ({ title: '', size: '', sizeUnit, type: '', floors: '', facing: '' });

  // Adds a new floor plan, or saves changes to the one being edited - matching mobile app
  const handleAddFloorPlan = () => {
    const { currentFloorPlan, currentFloorPlanImage, projectType, editingFloorPlanId } = formData;
    if (!currentFloorPlanImage) { toast.warning('Upload floor plan image'); return; }
    if (!currentFloorPlan.title) { toast.warning('Enter floor plan title'); return; }
    if (!currentFloorPlan.size) { toast.warning('Enter floor plan size'); return; }
    if ((projectType === 'Apartments' || projectType === 'Villas') && !currentFloorPlan.type) {
      toast.warning('Select floor plan type'); return;
    }
    const plan = {
      ...currentFloorPlan,
      id: editingFloorPlanId || Date.now().toString(),
      // A newly picked file is uploaded on save; otherwise the existing image_url is kept
      imageFile: currentFloorPlanImage?.file || null,
      imagePreview: currentFloorPlanImage?.preview || null,
    };
    setFormData(prev => ({
      ...prev,
      floorPlans: editingFloorPlanId
        ? prev.floorPlans.map(p => (p.id === editingFloorPlanId ? plan : p))
        : [...prev.floorPlans, plan],
      currentFloorPlan: emptyFloorPlan(prev.currentFloorPlan.sizeUnit),
      currentFloorPlanImage: null,
      editingFloorPlanId: null,
    }));
    toast.success(editingFloorPlanId ? 'Floor plan updated' : 'Floor plan added');
  };

  const handleEditFloorPlan = (plan) => {
    const { id, imageFile, imagePreview, ...fields } = plan;
    setFormData(prev => ({
      ...prev,
      currentFloorPlan: { ...emptyFloorPlan(), ...fields },
      currentFloorPlanImage: imagePreview ? { file: imageFile || null, preview: imagePreview } : null,
      editingFloorPlanId: id,
    }));
    floorPlanFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const handleCancelEditFloorPlan = () => {
    setFormData(prev => ({
      ...prev,
      currentFloorPlan: emptyFloorPlan(prev.currentFloorPlan.sizeUnit),
      currentFloorPlanImage: null,
      editingFloorPlanId: null,
    }));
  };

  const removeFloorPlan = (id) => {
    setFormData(prev => ({
      ...prev,
      floorPlans: prev.floorPlans.filter(p => p.id !== id),
      // Deleting the plan being edited also leaves edit mode
      ...(prev.editingFloorPlanId === id && {
        currentFloorPlan: emptyFloorPlan(prev.currentFloorPlan.sizeUnit),
        currentFloorPlanImage: null,
        editingFloorPlanId: null,
      }),
    }));
  };

  // ─── USPs ─────────────────────────────────────────────────────────────────
  const addUsp = () => {
    if (formData.usps.length >= 6) { toast.warning('Maximum 6 USPs allowed'); return; }
    setFormData(prev => ({ ...prev, usps: [...prev.usps, { title: '', content: '' }] }));
  };
  const removeUsp = (idx) => setFormData(prev => ({ ...prev, usps: prev.usps.filter((_, i) => i !== idx) }));
  const updateUsp = (idx, field, val) => setFormData(prev => ({
    ...prev, usps: prev.usps.map((u, i) => i === idx ? { ...u, [field]: val } : u),
  }));

  // ─── Save/Upload ──────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!formData.projectName?.trim()) { toast.warning('Project name is required'); return; }
    if (!formData.projectType) { toast.warning('Project type is required'); return; }
    if (!formData.budget.min) { toast.warning('Minimum budget is required'); return; }
    if (!formData.thumbnail) { toast.warning('Thumbnail image is required'); return; }
    if (!formData.gallery?.length) { toast.warning('Upload at least one gallery image'); return; }
    if (!formData.floorPlans?.length) { toast.warning('Add at least one floor plan'); return; }

    setIsUploading(true);
    try {
      // Upload thumbnail
      let thumbnailUrl = null;
      if (formData.thumbnail) {
        if (formData.thumbnail.isExisting) {
          thumbnailUrl = formData.thumbnail.url?.replace('https://sandbox.rebax.in', '') || null;
        } else {
          const res = await propertyService.uploadImage(formData.thumbnail.file);
          thumbnailUrl = res?.data?.url || res?.url || null;
        }
      }

      // Upload gallery images
      const uploadedImages = [];
      for (const img of formData.gallery) {
        if (img.isExisting) {
          uploadedImages.push(img.url?.replace('https://sandbox.rebax.in', '') || img.url);
        } else {
          const res = await propertyService.uploadImage(img.file);
          const url = res?.data?.url || res?.url;
          if (url) uploadedImages.push(url);
        }
      }

      // Upload payment plan PDF
      let paymentPlanUrl = null;
      if (formData.paymentPlans) {
        if (formData.paymentPlans.isExisting) {
          paymentPlanUrl = formData.paymentPlans.url?.replace('https://sandbox.rebax.in', '') || null;
        } else {
          const res = await propertyService.uploadDocument(formData.paymentPlans.file);
          paymentPlanUrl = res?.data?.url || res?.url || null;
        }
      }

      // Upload terms PDF
      let termsUrl = null;
      if (formData.termsAndConditions) {
        if (formData.termsAndConditions.isExisting) {
          termsUrl = formData.termsAndConditions.url?.replace('https://sandbox.rebax.in', '') || null;
        } else {
          const res = await propertyService.uploadDocument(formData.termsAndConditions.file);
          termsUrl = res?.data?.url || res?.url || null;
        }
      }

      // Upload floor plan images
      const floorPlanData = [];
      for (const plan of formData.floorPlans) {
        const { imageFile, imagePreview, ...rest } = plan;
        if (imageFile) {
          const res = await propertyService.uploadImage(imageFile);
          const imgUrl = res?.data?.url || res?.url || null;
          floorPlanData.push({ ...rest, image_url: imgUrl });
        } else {
          floorPlanData.push({ ...rest, image_url: plan.image_url || (imagePreview?.replace('https://sandbox.rebax.in', '')) || null });
        }
      }

      const mappedType = propertyTypeMap[formData.projectType] || { type: 'residential', subtype: null };
      const city = formData.addressCity || (formData.location?.split(',').map(s => s.trim()).filter(Boolean)[0]) || 'Unknown';

      const projectPayload = {
        property_title: formData.projectName.trim(),
        property_type: mappedType.type,
        property_subtype: mappedType.subtype,
        listing_type: 'sale',
        price: parseFloat(formData.budget.min) || 0,
        price_max: parseFloat(formData.budget.max) || parseFloat(formData.budget.min) || 0,
        price_per_sqft: parseFloat(formData.pricePerSqFt) || 0,
        city,
        state: formData.addressState || 'Telangana',
        address: formData.location || '',
        locality: formData.areaName || formData.addressLocality || city,
        pincode: formData.addressPincode || '',
        latitude: parseFloat(formData.latitude) || 17.4481,
        longitude: parseFloat(formData.longitude) || 78.3908,
        description: formData.projectOverview || '',
        area_sqft: parseFloat(formData.projectArea) || 0,
        total_floors: parseInt(formData.noOfFloors) || 0,
        furnishing_status: 'unfurnished',
        amenities: formData.selectedAmenities.map(id => {
          for (const cat of Object.values(amenitiesData)) {
            const a = cat.find(a => a.id === id);
            if (a) return a.name;
          }
          return null;
        }).filter(Boolean),
        features: formData.usps.map(u => u.title).filter(Boolean),
        usp: formData.usps.filter(u => u.title).map(u => ({ title: u.title, content: u.content || '' })),
        status: 'active',
        community_type: formData.projectCommunity || null,
        project_status: formData.projectStatus || null,
        completion_date: formData.completionDate || null,
        rera_id: formData.reraId || null,
        no_of_units: parseInt(formData.noOfUnits) || 0,
        no_of_towers: parseInt(formData.noOfTowers) || 0,
        amenities_cost: parseFloat(formData.amenitiesCost) || 0,
        car_parking_cost: parseFloat(formData.carParkingCost) || 0,
        extra_car_parking_cost: parseFloat(formData.extraCarParkingCost) || 0,
        facing: formData.facing.length > 0 ? formData.facing.join(',') : null,
        facing_charges: Object.keys(formData.facingCharges).length > 0 ? JSON.stringify(formData.facingCharges) : null,
        corner_plot: false,
        corner_premium: parseFloat(formData.cornerPremium) || 0,
        floor_rise: parseFloat(formData.floorRiseCharges) || 0,
        floor_rise_from_floor: parseInt(formData.floorRiseFromFloor) || 0,
        corpus_fund: parseFloat(formData.corpusFund) || 0,
        maintenance_cost: parseFloat(formData.maintenanceCost) || 0,
        possession_date: formData.possessionDate || null,
        agent_commission: parseFloat(formData.agentCommission) || 0,
        payout_after: parseFloat(formData.payoutAfter) || 0,
        images: uploadedImages,
        thumbnail: thumbnailUrl || (uploadedImages.length > 0 ? uploadedImages[0] : null),
        payment_plan_url: paymentPlanUrl,
        terms_url: termsUrl,
        floor_plans: floorPlanData,
      };

      let response;
      if (isEditMode && initialProject?.id) {
        response = await adminCrmService.updateProjectForBuilder(userId, initialProject.id, projectPayload);
      } else {
        response = await adminCrmService.createProjectForBuilder(userId, projectPayload);
      }

      if (response?.success || response?.data) {
        if (!isEditMode) localStorage.removeItem('projectLocation');
        const project = response?.data?.approval_status ? response.data : (response?.data?.data || response?.data);
        const projId = project?.id || initialProject?.id;
        setSavedProjectId(projId);
        const currentStatus = project?.approval_status || initialProject?.approval_status;
        if (isEditMode && currentStatus === 'approved') {
          toast.success('Project updated successfully!');
          navigate('/AdminCrmManageProjects', { state: { userId, userName, userRole, userEmail, userPhone } });
        } else {
          setApprovalModalVisible(true);
        }
      } else {
        toast.error(response?.message || 'Failed to save project');
      }
    } catch (err) {
      console.error('Save project error:', err);
      toast.error(err?.response?.data?.message || err?.message || 'Failed to save project. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  // ─── Approval Actions ─────────────────────────────────────────────────────
  const handleApproveNow = async () => {
    if (!savedProjectId) return;
    setApprovalLoading(true);
    try {
      const res = await adminCrmService.approveProjectFromCrm(userId, savedProjectId);
      if (res?.success || res?.data) {
        toast.success('Project has been approved successfully!');
        setApprovalModalVisible(false);
        navigate('/AdminCrmManageProjects', { state: { userId, userName, userRole, userEmail, userPhone } });
      } else {
        toast.error(res?.message || 'Failed to approve project');
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to approve project');
    } finally {
      setApprovalLoading(false);
    }
  };

  const handleSendToTask = async () => {
    if (!savedProjectId) return;
    setApprovalLoading(true);
    try {
      const res = await adminCrmService.sendProjectToTask(userId, savedProjectId);
      if (res?.success || res?.data) {
        toast.success('Project sent to verification task. It will appear in the Tasks section for review.');
        setApprovalModalVisible(false);
        navigate('/AdminCrmManageProjects', { state: { userId, userName, userRole, userEmail, userPhone } });
      } else {
        toast.error(res?.message || 'Failed to send to task');
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to send to task');
    } finally {
      setApprovalLoading(false);
    }
  };

  // ─── Helpers ──────────────────────────────────────────────────────────────
  // ─── Map Handlers ─────────────────────────────────────────────────────────
  const openMap = () => {
    const lat = parseFloat(formData.latitude) || 17.4481;
    const lng = parseFloat(formData.longitude) || 78.3908;
    setMarkerPos({ lat, lng });
    setMapCenter({ lat, lng });
    setMapSearch('');
    setMapSearchResults([]);
    setMapVisible(true);
  };

  const handleLocationSearch = (query) => {
    if (!query.trim() || !window.google?.maps?.places) {
      setMapSearchResults([]);
      return;
    }
    setIsMapSearching(true);
    try {
      const autocompleteService = new window.google.maps.places.AutocompleteService();
      autocompleteService.getPlacePredictions(
        { input: query, componentRestrictions: { country: 'in' } },
        (predictions, status) => {
          setIsMapSearching(false);
          if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions?.length) {
            setMapSearchResults(predictions.slice(0, 5));
          } else {
            setMapSearchResults([]);
          }
        }
      );
    } catch {
      setIsMapSearching(false);
      setMapSearchResults([]);
    }
  };

  // Debounced suggestions once the user has typed at least 3 characters (project name or location)
  useEffect(() => {
    if (!mapVisible) {
      setMapSearchResults([]);
      return;
    }
    if (mapSearch.trim().length > 2) {
      const timeoutId = setTimeout(() => handleLocationSearch(mapSearch), 500);
      return () => clearTimeout(timeoutId);
    }
    setMapSearchResults([]);
  }, [mapSearch, mapVisible]); // eslint-disable-line

  const selectMapSearchResult = (prediction) => {
    setMapSearchResults([]);
    setMapSearch(prediction.description || '');
    if (!window.google?.maps) return;
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ placeId: prediction.place_id }, (results, status) => {
      if (status === 'OK' && results?.[0]) {
        const loc = results[0].geometry.location;
        const position = {
          lat: typeof loc.lat === 'function' ? loc.lat() : loc.lat,
          lng: typeof loc.lng === 'function' ? loc.lng() : loc.lng,
        };
        setMarkerPos(position);
        setMapCenter(position);
      } else {
        toast.error('Unable to locate that place');
      }
      setMapSearch('');
    });
  };

  const confirmMapLocation = () => {
    upd('latitude', markerPos.lat);
    upd('longitude', markerPos.lng);

    if (!window.google?.maps) {
      setMapVisible(false);
      return;
    }

    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ location: markerPos }, (results, status) => {
      if (status === 'OK' && results?.[0]) {
        const result = results[0];
        const components = result.address_components || [];
        let sublocality = '', sublocality2 = '', neighborhood = '', city = '', state = '', pincode = '';

        components.forEach(comp => {
          const types = comp.types || [];
          if (types.includes('sublocality_level_1') || types.includes('sublocality')) {
            sublocality = comp.long_name;
          } else if (types.includes('sublocality_level_2')) {
            sublocality2 = comp.long_name;
          } else if (types.includes('neighborhood')) {
            neighborhood = comp.long_name;
          } else if (types.includes('locality') || types.includes('administrative_area_level_2')) {
            city = comp.long_name;
          } else if (types.includes('administrative_area_level_1')) {
            state = comp.long_name;
          } else if (types.includes('postal_code')) {
            pincode = comp.long_name;
          }
        });

        // e.g. for "Rahmath Manzil, Cyberhills Colony, P Janardhan Reddy Nagar,
        // Gachibowli, Hyderabad, Telangana 500032" this picks up "Gachibowli"
        let areaName = sublocality || sublocality2 || neighborhood || '';

        // Fallback: parse the area from the formatted address string
        if (!areaName && result.formatted_address) {
          const parts = result.formatted_address.split(',').map(p => p.trim());
          if (parts.length >= 4) areaName = parts[parts.length - 4];
          else if (parts.length >= 3) areaName = parts[0];
        }

        setFormData(prev => ({
          ...prev,
          location: result.formatted_address || '',
          areaName: areaName || prev.areaName,
          addressLocality: areaName || prev.addressLocality,
          addressCity: city || prev.addressCity,
          addressState: state || prev.addressState,
          addressPincode: pincode || prev.addressPincode,
        }));
      } else {
        toast.error('Could not fetch address for this location');
      }
      setMapVisible(false);
    });
  };

  const toggleFacing = (f) => {
    setFormData(prev => ({
      ...prev,
      facing: prev.facing.includes(f) ? prev.facing.filter(x => x !== f) : [...prev.facing, f],
    }));
  };

  const setFacingCharge = (f, field, val) => {
    setFormData(prev => ({
      ...prev,
      facingCharges: {
        ...prev.facingCharges,
        [f]: { ...prev.facingCharges[f], [field]: val, unit: prev.facingCharges[f]?.unit || 'SFT' },
      },
    }));
  };

  const toggleFacingUnit = (f) => {
    setFormData(prev => ({
      ...prev,
      facingCharges: {
        ...prev.facingCharges,
        [f]: { ...prev.facingCharges[f], unit: (prev.facingCharges[f]?.unit || 'SFT') === 'SFT' ? 'SQYD' : 'SFT' },
      },
    }));
  };

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="d-flex" style={{ background: '#F9FAFB', minHeight: '100vh' }}>
      <LeftNav />
      <div className="main-content flex-grow-1">
        <TopNav />

        {/* Hidden file inputs */}
        <input ref={thumbnailRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={e => handleImageFile('thumbnail', e)} />
        <input ref={galleryRef} type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={handleGalleryFiles} />
        <input ref={floorPlanImgRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={e => handleImageFile('floorPlan', e)} />
        <input ref={paymentPlanRef} type="file" accept="application/pdf" style={{ display: 'none' }} onChange={e => handleDocFile('payment', e)} />
        <input ref={termsRef} type="file" accept="application/pdf" style={{ display: 'none' }} onChange={e => handleDocFile('terms', e)} />

        <div className="p-4" style={{ paddingBottom: 80 }}>

          {/* Header */}
          <div className="d-flex align-items-center justify-content-between mb-4">
            <div className="d-flex align-items-center gap-2">
              <img src={arrowleftt} onClick={() => navigate(-1)} style={{ cursor: 'pointer' }} alt="back" />
              <div>
                <h6 className="accordion-title mb-0">{isEditMode ? 'Edit Project' : 'Add Project'}</h6>
                {userName && <div style={{ fontSize: 12, color: '#6B7280' }}>Builder: {userName}</div>}
              </div>
            </div>
          </div>

          {isLoadingProject && (
            <div style={{ textAlign: 'center', padding: 40, color: '#6B7280' }}>Loading project data...</div>
          )}

          {/* ── 1. Basic Information ── */}
          <div style={S.section}>
            <div style={S.sectionTitle}>Basic Information</div>
            <Field label="Project Name" value={formData.projectName} onChange={v => upd('projectName', v)} placeholder="Enter project name" required />
          </div>

          {/* ── 2. Commission Details ── */}
          <div style={S.section}>
            <div style={S.sectionTitle}>Commission Details</div>
            <div className="row">
              <div className="col-6">
                <div style={S.fieldWrap}>
                  <label style={S.label}>Agent Commission %</label>
                  <div style={S.unitRow}>
                    <input style={S.unitInput} type="number" value={formData.agentCommission}
                      onChange={e => upd('agentCommission', e.target.value)} placeholder="0" />
                    <span style={{ padding: '0 14px', backgroundColor: '#F3F4F6', color: '#6B7280', fontSize: 12, display: 'flex', alignItems: 'center' }}>%</span>
                  </div>
                </div>
              </div>
              <div className="col-6">
                <div style={S.fieldWrap}>
                  <label style={S.label}>Payout After %</label>
                  <div style={S.unitRow}>
                    <input style={S.unitInput} type="number" value={formData.payoutAfter}
                      onChange={e => upd('payoutAfter', e.target.value)} placeholder="0" />
                    <span style={{ padding: '0 14px', backgroundColor: '#F3F4F6', color: '#6B7280', fontSize: 12, display: 'flex', alignItems: 'center' }}>%</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Terms & Conditions PDF */}
            <div style={S.fieldWrap}>
              <label style={S.label}>Terms & Conditions</label>
              {formData.termsAndConditions ? (
                <div style={S.docCard}>
                  <span style={{ fontSize: 18 }}>📄</span>
                  <span style={{ flex: 1, fontSize: 13, color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {formData.termsAndConditions.name}
                  </span>
                  <button type="button" onClick={() => upd('termsAndConditions', null)}
                    style={{ background: 'none', border: 'none', color: '#EF4444', cursor: 'pointer', fontSize: 16 }}>🗑</button>
                </div>
              ) : (
                <div style={S.uploadBox} onClick={() => termsRef.current?.click()}>
                  <div style={{ fontSize: 24, color: '#9CA3AF' }}>📄</div>
                  <div style={{ fontSize: 13, color: '#9CA3AF', marginTop: 4 }}>Upload <span style={{ color: '#2563EB' }}>Terms & Conditions</span></div>
                  <div style={{ fontSize: 11, color: '#D1D5DB', marginTop: 2 }}>PDF only | Max 25MB</div>
                </div>
              )}
            </div>
          </div>

          {/* ── 3. Project Overview ── */}
          <div style={S.section}>
            <div style={S.sectionTitle}>Project Overview</div>
            <div style={S.fieldWrap}>
              <label style={S.label}>Description</label>
              <textarea style={{ ...S.input, minHeight: 100, resize: 'vertical' }}
                value={formData.projectOverview} maxLength={900}
                onChange={e => { if (e.target.value.length <= 900) upd('projectOverview', e.target.value); }}
                placeholder="Describe the project..." />
              <div style={{ fontSize: 11, color: '#9CA3AF', textAlign: 'right', marginTop: 4 }}>{formData.projectOverview.length}/900</div>
            </div>

            {/* USPs */}
            <div style={S.fieldWrap}>
              <label style={{ ...S.label, marginBottom: 8 }}>Unique Selling Points</label>
              {formData.usps.map((usp, idx) => (
                <div key={idx} style={S.uspCard}>
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span style={{ fontSize: 11, fontWeight: 600, color: '#6B7280' }}>USP {idx + 1}</span>
                    {formData.usps.length > 1 && (
                      <button type="button" onClick={() => removeUsp(idx)}
                        style={{ background: 'none', border: 'none', color: '#EF4444', cursor: 'pointer', fontSize: 16 }}>✕</button>
                    )}
                  </div>
                  <input style={{ ...S.input, marginBottom: 6 }} value={usp.title}
                    onChange={e => updateUsp(idx, 'title', e.target.value)} placeholder="USP Title" />
                  <textarea style={{ ...S.input, minHeight: 50, resize: 'vertical' }}
                    value={usp.content} maxLength={300}
                    onChange={e => { if (e.target.value.length <= 300) updateUsp(idx, 'content', e.target.value); }}
                    placeholder="USP Description (optional)" />
                  <div style={{ fontSize: 10, color: '#9CA3AF', textAlign: 'right', marginTop: 2 }}>{(usp.content || '').length}/300</div>
                </div>
              ))}
              <div style={{ display: 'flex', justifyContent: 'flex-start', marginTop: 12 }}>
                <button type="button" onClick={addUsp}
                  style={{ padding: '10px 16px', borderRadius: 8, border: '1px solid #2563EB', backgroundColor: '#EFF6FF', color: '#2563EB', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>+ Add USP</button>
              </div>
            </div>
          </div>

          {/* ── 4. Property Specifications ── */}
          <div style={S.section}>
            <div style={S.sectionTitle}>Property Specifications</div>

            <div style={S.fieldWrap}>
              <label style={S.label}>Project Type <span style={{ color: '#EF4444' }}>*</span></label>
              <select style={S.select} value={formData.projectType}
                onChange={e => {
                  const v = e.target.value;
                  const defaultUnit = isPlotLikeType(v) ? 'SQYD' : 'SFT';
                  setFormData(prev => ({
                    ...prev, projectType: v, projectCommunity: '',
                    priceUnit: defaultUnit, cornerPremiumUnit: defaultUnit,
                    floorRiseUnit: defaultUnit, corpusFundUnit: defaultUnit, maintenanceUnit: defaultUnit,
                    currentFloorPlan: { ...prev.currentFloorPlan, sizeUnit: defaultUnit },
                  }));
                }}>
                <option value="">Select Project Type</option>
                {projectTypeOptions.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>

            <div style={S.fieldWrap}>
              <label style={S.label}>Community Type</label>
              <select style={S.select} value={formData.projectCommunity} onChange={e => upd('projectCommunity', e.target.value)}>
                <option value="">{communityLoading ? 'Loading...' : 'Select Community Type'}</option>
                {communityOptions.map((c, i) => <option key={i} value={c}>{c}</option>)}
              </select>
            </div>

            <div style={S.fieldWrap}>
              <label style={S.label}>Project Status</label>
              <select style={S.select} value={formData.projectStatus} onChange={e => upd('projectStatus', e.target.value)}>
                <option value="">Select Status</option>
                {projectStatusOptions.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>

            <div className="row">
              <div className="col-6">
                <Field label="Possession Date" value={formData.possessionDate} type="date"
                  onChange={v => upd('possessionDate', v)} placeholder="" />
              </div>
              <div className="col-6">
                <Field label="Completion Date" value={formData.completionDate} type="date"
                  onChange={v => upd('completionDate', v)} placeholder="" />
              </div>
            </div>

            <Field label="Area (in Acres)" value={formData.projectArea} onChange={v => upd('projectArea', v)} placeholder="e.g. 15" type="number" />

            <UnitField label={`Price per ${formData.priceUnit}`} value={formData.pricePerSqFt}
              onChange={v => upd('pricePerSqFt', v)} unit={formData.priceUnit}
              onToggleUnit={() => upd('priceUnit', formData.priceUnit === 'SFT' ? 'SQYD' : 'SFT')}
              placeholder="0" showWords />

            <Field label="No of Units" value={formData.noOfUnits} onChange={v => upd('noOfUnits', v)} placeholder="0" type="number" />

            {!isPlotLikeType(formData.projectType) && (
              <div className="row">
                {(formData.projectType === 'Apartments' || formData.projectType === 'Commercial') && (
                  <div className={formData.projectType === 'Apartments' ? 'col-6' : 'col-12'}>
                    <Field label="No of Floors" value={formData.noOfFloors} onChange={v => upd('noOfFloors', v)} placeholder="0" type="number" />
                  </div>
                )}
                {formData.projectType === 'Apartments' && (
                  <div className="col-6">
                    <Field label="No of Towers" value={formData.noOfTowers} onChange={v => upd('noOfTowers', v)} placeholder="0" type="number" />
                  </div>
                )}
              </div>
            )}

            <Field label="RERA ID" value={formData.reraId} onChange={v => upd('reraId', v.toUpperCase())} placeholder="Enter RERA ID" />
          </div>

          {/* ── 5. Pricing ── */}
          <div style={S.section}>
            <div style={S.sectionTitle}>Pricing</div>

            <div className="row">
              <div className="col-6">
                <div style={S.fieldWrap}>
                  <label style={S.label}>Min Budget <span style={{ color: '#EF4444' }}>*</span></label>
                  <input style={S.input} type="number" value={formData.budget.min}
                    onChange={e => setFormData(prev => ({ ...prev, budget: { ...prev.budget, min: e.target.value } }))} placeholder="0" />
                  {formData.budget.min && <div style={S.hint}>{numberToIndianWords(formData.budget.min)}</div>}
                </div>
              </div>
              <div className="col-6">
                <div style={S.fieldWrap}>
                  <label style={S.label}>Max Budget</label>
                  <input style={S.input} type="number" value={formData.budget.max}
                    onChange={e => setFormData(prev => ({ ...prev, budget: { ...prev.budget, max: e.target.value } }))} placeholder="0" />
                  {formData.budget.max && <div style={S.hint}>{numberToIndianWords(formData.budget.max)}</div>}
                </div>
              </div>
            </div>

            <div className="row">
              <div className="col-6">
                <div style={S.fieldWrap}>
                  <label style={S.label}>Amenities Cost</label>
                  <input style={S.input} type="number" value={formData.amenitiesCost}
                    onChange={e => upd('amenitiesCost', e.target.value)} placeholder="0" />
                  {formData.amenitiesCost && <div style={S.hint}>{numberToIndianWords(formData.amenitiesCost)}</div>}
                </div>
              </div>
              <div className="col-6">
                <div style={S.fieldWrap}>
                  <label style={S.label}>Car Parking Cost</label>
                  <input style={S.input} type="number" value={formData.carParkingCost}
                    onChange={e => upd('carParkingCost', e.target.value)} placeholder="0" />
                  {formData.carParkingCost && <div style={S.hint}>{numberToIndianWords(formData.carParkingCost)}</div>}
                </div>
              </div>
            </div>

            <div style={S.fieldWrap}>
              <label style={S.label}>Extra Car Parking Cost</label>
              <input style={S.input} type="number" value={formData.extraCarParkingCost}
                onChange={e => upd('extraCarParkingCost', e.target.value)} placeholder="0" />
              {formData.extraCarParkingCost && <div style={S.hint}>{numberToIndianWords(formData.extraCarParkingCost)}</div>}
            </div>

            {/* Facing multi-select */}
            <div style={S.fieldWrap}>
              <label style={S.label}>Facing</label>
              <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                {facingOptions.map(f => (
                  <button key={f} type="button"
                    style={formData.facing.includes(f) ? S.chipActive : S.chip}
                    onClick={() => toggleFacing(f)}>
                    {f}
                  </button>
                ))}
              </div>
            </div>

            {/* Facing Charges */}
            {formData.facing.length > 0 && (
              <div style={S.fieldWrap}>
                <label style={S.label}>Facing Charges</label>
                {formData.facing.map(f => (
                  <div key={f} style={{ marginBottom: 12 }}>
                    <div style={{ fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 4 }}>{f}</div>
                    <div style={S.unitRow}>
                      <input style={S.unitInput} type="number" placeholder="Enter amount"
                        value={formData.facingCharges[f]?.amount || ''}
                        onChange={e => setFacingCharge(f, 'amount', e.target.value)} />
                      <span style={{ padding: '0 8px', color: '#9CA3AF', fontSize: 12, alignSelf: 'center' }}>per</span>
                      <button style={S.unitBtn} type="button" onClick={() => toggleFacingUnit(f)}>
                        {formData.facingCharges[f]?.unit || 'SFT'} ▾
                      </button>
                    </div>
                    {formData.facingCharges[f]?.amount && parseFloat(formData.facingCharges[f].amount) > 0 && (
                      <div style={S.hint}>{numberToIndianWords(formData.facingCharges[f].amount)}</div>
                    )}
                  </div>
                ))}
              </div>
            )}
            {formData.facing.length === 0 && (
              <div style={{ fontSize: 12, color: '#9CA3AF', fontStyle: 'italic', marginBottom: 14 }}>
                Select facing directions above to add charges
              </div>
            )}

            {/* Corner Charges */}
            <UnitField label="Corner Charges" value={formData.cornerPremium}
              onChange={v => upd('cornerPremium', v)} unit={formData.cornerPremiumUnit}
              onToggleUnit={() => upd('cornerPremiumUnit', formData.cornerPremiumUnit === 'SFT' ? 'SQYD' : 'SFT')}
              placeholder="Enter amount" perLabel showWords />

            {/* Floor Rise Charges */}
            <UnitField label="Floor Rise Charges" value={formData.floorRiseCharges}
              onChange={v => upd('floorRiseCharges', v)} unit={formData.floorRiseUnit}
              onToggleUnit={() => upd('floorRiseUnit', formData.floorRiseUnit === 'SFT' ? 'SQYD' : 'SFT')}
              placeholder="Floor rise charges" showWords />

            <Field label="Floor Rise From Floor" value={formData.floorRiseFromFloor}
              onChange={v => upd('floorRiseFromFloor', v)} placeholder="e.g. 5" type="number" />

            {/* Corpus Fund */}
            <UnitField label="Corpus Fund Amount" value={formData.corpusFund}
              onChange={v => upd('corpusFund', v)} unit={formData.corpusFundUnit}
              onToggleUnit={() => upd('corpusFundUnit', formData.corpusFundUnit === 'SFT' ? 'SQYD' : 'SFT')}
              placeholder="Corpus fund" showWords />

            {/* Maintenance */}
            <div style={S.fieldWrap}>
              <label style={S.label}>Maintenance per year</label>
              <div style={S.unitRow}>
                <input style={S.unitInput} type="number" value={formData.maintenanceCost}
                  onChange={e => upd('maintenanceCost', e.target.value)} placeholder="Cost" />

                <button style={S.unitBtn} type="button"
                  onClick={() => upd('maintenanceUnit', formData.maintenanceUnit === 'SFT' ? 'SQYD' : 'SFT')}>
                  {formData.maintenanceUnit} ▾
                </button>
              </div>
              {formData.maintenanceCost && <div style={S.hint}>{numberToIndianWords(formData.maintenanceCost)}</div>}
            </div>
          </div>

          {/* ── 6. Floor Plans ── */}
          <div style={S.section}>
            <div style={S.sectionTitle}>Floor Plans</div>

            {/* Existing floor plans */}
            {formData.floorPlans.map((plan, idx) => (
              <div key={plan.id || idx}
                style={{ ...S.floorCard, ...(formData.editingFloorPlanId === plan.id && { borderColor: '#2563EB', boxShadow: '0 0 0 2px rgba(37,99,235,0.15)' }) }}>
                <div className="d-flex justify-content-between align-items-start">
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>{plan.title}</div>
                    <div style={{ fontSize: 12, color: '#6B7280', marginTop: 2 }}>
                      {plan.size} {plan.sizeUnit}{plan.type ? ` | ${plan.type}` : ''}{plan.facing ? ` | ${plan.facing}` : ''}
                    </div>
                  </div>
                  <div className="d-flex gap-2">
                    <button type="button" onClick={() => handleEditFloorPlan(plan)} title="Edit floor plan"
                      style={{ background: 'none', border: 'none', color: '#6B7280', cursor: 'pointer', fontSize: 16 }}>✏️</button>
                    <button type="button" onClick={() => removeFloorPlan(plan.id)} title="Delete floor plan"
                      style={{ background: 'none', border: 'none', color: '#EF4444', cursor: 'pointer', fontSize: 16 }}>🗑</button>
                  </div>
                </div>
                {(plan.imagePreview || plan.image_url) && (
                  <img src={plan.imagePreview || getFullUrl(plan.image_url)} alt="floor plan"
                    style={{ width: '100%', height: 120, objectFit: 'cover', borderRadius: 8, marginTop: 8 }} />
                )}
              </div>
            ))}

            {/* Add new floor plan */}
            <div style={S.addFloorBox} ref={floorPlanFormRef}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 10 }}>
                {formData.editingFloorPlanId ? 'Edit Floor Plan' : 'Add New Floor Plan'}
              </div>

              {/* Floor plan image */}
              <div style={S.fieldWrap}>
                <label style={S.label}>Floor Plan Image<span style={{ color: '#EF4444' }}>*</span></label>
                {formData.currentFloorPlanImage ? (
                  <div style={{ position: 'relative' }}>
                    <img src={formData.currentFloorPlanImage.preview} alt="floor plan preview"
                      style={{ width: '100%', height: 100, objectFit: 'cover', borderRadius: 8, cursor: 'pointer' }}
                      onClick={() => floorPlanImgRef.current?.click()} />
                    <button type="button"
                      onClick={() => upd('currentFloorPlanImage', null)}
                      style={{ position: 'absolute', top: 4, right: 4, background: '#fff', border: 'none', borderRadius: '50%', cursor: 'pointer', fontSize: 16, color: '#EF4444' }}>✕</button>
                  </div>
                ) : (
                  <div style={S.uploadBox} onClick={() => floorPlanImgRef.current?.click()}>
                    <div style={{ fontSize: 20, color: '#9CA3AF' }}>🖼</div>
                    <div style={{ fontSize: 12, color: '#9CA3AF', marginTop: 4 }}>Upload Floor Plan Image</div>
                  </div>
                )}
              </div>

              <Field label="Title" value={formData.currentFloorPlan.title}
                onChange={v => setFormData(prev => ({ ...prev, currentFloorPlan: { ...prev.currentFloorPlan, title: v } }))}
                placeholder="e.g. 2 BHK East" />

              <div className="row">
                <div className="col-6">
                  <div style={S.fieldWrap}>
                    <label style={S.label}>Size</label>
                    <div style={S.unitRow}>
                      <input style={S.unitInput} type="number" placeholder="0"
                        value={formData.currentFloorPlan.size}
                        onChange={e => setFormData(prev => ({ ...prev, currentFloorPlan: { ...prev.currentFloorPlan, size: e.target.value } }))} />
                      <button style={S.unitBtn} type="button"
                        onClick={() => setFormData(prev => ({ ...prev, currentFloorPlan: { ...prev.currentFloorPlan, sizeUnit: prev.currentFloorPlan.sizeUnit === 'SFT' ? 'SQYD' : 'SFT' } }))}>
                        {formData.currentFloorPlan.sizeUnit} ▾
                      </button>
                    </div>
                  </div>
                </div>
                {(formData.projectType === 'Apartments' || formData.projectType === 'Villas') && (
                  <div className="col-6">
                    <div style={S.fieldWrap}>
                      <label style={S.label}>Type (BHK)</label>
                      <select style={S.select} value={formData.currentFloorPlan.type}
                        onChange={e => setFormData(prev => ({ ...prev, currentFloorPlan: { ...prev.currentFloorPlan, type: e.target.value } }))}>
                        <option value="">Select Type</option>
                        {bhkTypeOptions.map(b => <option key={b} value={b}>{b}</option>)}
                      </select>
                    </div>
                  </div>
                )}
              </div>

              <div className="row">
                {(formData.projectType === 'Villas' || formData.projectType === 'Commercial') && (
                  <div className="col-6">
                    <Field label="No of Floors" value={formData.currentFloorPlan.floors}
                      onChange={v => setFormData(prev => ({ ...prev, currentFloorPlan: { ...prev.currentFloorPlan, floors: v } }))}
                      placeholder="0" type="number" />
                  </div>
                )}
                <div className={formData.projectType === 'Villas' || formData.projectType === 'Commercial' ? 'col-6' : 'col-12'}>
                  <div style={S.fieldWrap}>
                    <label style={S.label}>Facing</label>
                    <select style={S.select} value={formData.currentFloorPlan.facing}
                      onChange={e => setFormData(prev => ({ ...prev, currentFloorPlan: { ...prev.currentFloorPlan, facing: e.target.value } }))}>
                      <option value="">Select Facing</option>
                      {facingOptions.map(f => <option key={f} value={f}>{f}</option>)}
                    </select>
                  </div>
                </div>
              </div>

              <button type="button" onClick={handleAddFloorPlan}
                style={{ width: '100%', padding: '10px', backgroundColor: '#2563EB', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer', marginTop: 4 }}>
                {formData.editingFloorPlanId ? '✓ Update Floor Plan' : '+ Add Floor Plan'}
              </button>
              {formData.editingFloorPlanId && (
                <button type="button" onClick={handleCancelEditFloorPlan}
                  style={{ width: '100%', padding: '8px', background: 'none', color: '#EF4444', border: 'none', fontSize: 13, fontWeight: 600, cursor: 'pointer', marginTop: 4 }}>
                  Cancel Edit
                </button>
              )}
            </div>
          </div>

          {/* ── 7. Amenities ── */}
          <div style={S.section}>
            <div style={S.sectionTitle}>Amenities</div>

            {/* Tabs */}
            <div style={{ display: 'flex', overflowX: 'auto', marginBottom: 14, paddingBottom: 4 }}>
              {amenityTabs.map(tab => (
                <button key={tab} type="button"
                  onClick={() => setSelectedAmenityTab(tab)}
                  style={{
                    padding: '7px 14px', borderRadius: 20, border: 'none', marginRight: 8, cursor: 'pointer', whiteSpace: 'nowrap', fontSize: 12, fontWeight: 500,
                    backgroundColor: selectedAmenityTab === tab ? '#2563EB' : '#F3F4F6',
                    color: selectedAmenityTab === tab ? '#fff' : '#6B7280',
                  }}>
                  {tab}
                </button>
              ))}
            </div>

            {/* Amenity grid */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {(amenitiesData[selectedAmenityTab] || []).map(item => {
                const isSelected = formData.selectedAmenities.includes(item.id);
                const iconColor = isSelected ? '#2563EB' : '#9CA3AF';
                return (
                  <button key={item.id} type="button"
                    style={isSelected ? S.amenityItemActive : S.amenityItem}
                    onClick={() => setFormData(prev => ({
                      ...prev,
                      selectedAmenities: isSelected
                        ? prev.selectedAmenities.filter(id => id !== item.id)
                        : [...prev.selectedAmenities, item.id],
                    }))}>
                    {item.Icon && <item.Icon size={20} color={iconColor} style={{ marginBottom: 4 }} />}
                    <span>{item.name}</span>
                  </button>
                );
              })}
            </div>
            {formData.selectedAmenities.length > 0 && (
              <div style={{ fontSize: 12, color: '#2563EB', fontWeight: 600, marginTop: 10, textAlign: 'center' }}>
                {formData.selectedAmenities.length} amenities selected
              </div>
            )}
          </div>

          {/* ── 8. Location ── */}
          <div style={S.section}>
            <div style={S.sectionTitle}>Location</div>

            <Field label="Area / Locality" value={formData.areaName}
              onChange={v => setFormData(prev => ({ ...prev, areaName: v, addressLocality: v }))}
              placeholder="e.g. Gachibowli" />

            {formData.latitude && formData.longitude && (
              <div
                style={{
                  height: 240,
                  borderRadius: 10,
                  overflow: 'hidden',
                  border: '1px solid #E5E7EB',
                  marginBottom: 14,
                }}
              >
                {mapsLoaded ? (
                  <GoogleMap
                    mapContainerStyle={{ width: '100%', height: '100%' }}
                    center={{
                      lat: Number(formData.latitude),
                      lng: Number(formData.longitude)
                    }}
                    zoom={15}
                    options={{ disableDefaultUI: true, zoomControl: true }}
                  >
                    <GoogleMarker
                      position={{
                        lat: Number(formData.latitude),
                        lng: Number(formData.longitude)
                      }}
                    />
                  </GoogleMap>
                ) : (
                  <div
                    style={{
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#6B7280'
                    }}
                  >
                    Loading map...
                  </div>
                )}
              </div>
            )}

            {formData.location && (
              <div
                style={{
                  padding: '8px 6px',
                  fontSize: 14,
                  color: '#374151',
                  fontWeight: 500,
                  wordBreak: 'break-word'
                }}
              >
                {formData.location}
              </div>
            )}

            {/* Pick on Map */}
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: 6,
              }}
            >
              <button
                type="button"
                onClick={openMap}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '12px 20px',
                  borderRadius: 8,
                  border: '1px solid #2563EB',
                  backgroundColor: '#EFF6FF',
                  color: '#2563EB',
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                <IoLocationSharp size={18} />
                Pick on Map
              </button>
              {formData.latitude && formData.longitude && (
                <div style={{ fontSize: 11, color: '#9CA3AF' }}>
                  Lat: {Number(formData.latitude).toFixed(6)}, Lng: {Number(formData.longitude).toFixed(6)}
                </div>
              )}
            </div>
          </div>

          {/* ── 9. Gallery & Media ── */}
          <div style={S.section}>
            <div style={S.sectionTitle}>Gallery & Media</div>

            {/* Thumbnail */}
            <div style={S.fieldWrap}>
              <label style={S.label}>Thumbnail Image <span style={{ color: '#EF4444' }}>*</span></label>
              {formData.thumbnail ? (
                <div style={{ position: 'relative', display: 'inline-block', width: '100%' }}>
                  <img src={formData.thumbnail.preview || formData.thumbnail.url} alt="thumbnail"
                    style={{ width: '100%', height: 160, objectFit: 'cover', borderRadius: 10, cursor: 'pointer' }}
                    onClick={() => thumbnailRef.current?.click()} />
                  <button type="button" onClick={() => upd('thumbnail', null)}
                    style={{ position: 'absolute', top: 6, right: 6, background: '#fff', border: 'none', borderRadius: '50%', cursor: 'pointer', fontSize: 16, color: '#EF4444', width: 28, height: 28 }}>✕</button>
                </div>
              ) : (
                <div style={S.uploadBox} onClick={() => thumbnailRef.current?.click()}>
                  <div style={{ fontSize: 28, color: '#9CA3AF' }}>🖼</div>
                  <div style={{ fontSize: 13, color: '#9CA3AF', marginTop: 4 }}>Upload Thumbnail</div>
                </div>
              )}
            </div>

            {/* Gallery */}
            <div style={S.fieldWrap}>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <label style={{ ...S.label, marginBottom: 0 }}>Gallery Images <span style={{ color: '#EF4444' }}>*</span></label>
                <button type="button" onClick={() => galleryRef.current?.click()}
                  style={{ background: 'none', border: 'none', color: '#2563EB', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>+ Add Image</button>
              </div>
              {formData.gallery.length > 0 ? (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 8 }}>
                  {formData.gallery.map((img, idx) => (
                    <div key={idx} style={{ position: 'relative' }}>
                      <img src={img.preview || img.url} alt="gallery"
                        style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 8 }} />
                      <button type="button"
                        onClick={() => setFormData(prev => ({ ...prev, gallery: prev.gallery.filter((_, i) => i !== idx) }))}
                        style={{ position: 'absolute', top: -6, right: -6, background: '#fff', border: 'none', borderRadius: '50%', cursor: 'pointer', fontSize: 14, color: '#EF4444', width: 22, height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }}>✕</button>
                    </div>
                  ))}
                  <div style={{ ...S.uploadBox, width: 80, height: 80, padding: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}
                    onClick={() => galleryRef.current?.click()}>
                    <span style={{ fontSize: 20, color: '#9CA3AF' }}>+</span>
                  </div>
                </div>
              ) : (
                <div style={S.uploadBox} onClick={() => galleryRef.current?.click()}>
                  <div style={{ fontSize: 28, color: '#9CA3AF' }}>🖼</div>
                  <div style={{ fontSize: 13, color: '#9CA3AF', marginTop: 4 }}>Upload Gallery Images</div>
                </div>
              )}
            </div>
          </div>

          {/* ── 10. Payment Plans ── */}
          <div style={S.section}>
            <div style={S.sectionTitle}>Payment Plans</div>
            {formData.paymentPlans ? (
              <div style={S.docCard}>
                <span style={{ fontSize: 18 }}>📄</span>
                <span style={{ flex: 1, fontSize: 13, color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {formData.paymentPlans.name}
                </span>
                <button type="button" onClick={() => upd('paymentPlans', null)}
                  style={{ background: 'none', border: 'none', color: '#6B7280', cursor: 'pointer', fontSize: 16 }}>🗑</button>
              </div>
            ) : (
              <div style={S.uploadBox} onClick={() => paymentPlanRef.current?.click()}>
                <div style={{ fontSize: 28, color: '#9CA3AF' }}>📄</div>
                <div style={{ fontSize: 13, color: '#9CA3AF', marginTop: 4 }}>Drag and Drop or choose File</div>
                <div style={{ fontSize: 11, color: '#D1D5DB', marginTop: 2 }}>Supported formats PDF | Max size 25MB</div>
              </div>
            )}
          </div>

          {/* ── Save Button ── */}
          <button
            type="button"
            onClick={handleSave}
            disabled={isUploading}
            style={{
              width: '100%', padding: 15, borderRadius: 12, border: 'none',
              backgroundColor: isUploading ? '#93C5FD' : '#2563EB', color: '#fff',
              fontSize: 16, fontWeight: 700, cursor: isUploading ? 'not-allowed' : 'pointer',
              marginTop: 8, boxShadow: '0 4px 12px rgba(37,99,235,0.3)',
            }}>
            {isUploading ? (isEditMode ? 'Updating...' : 'Uploading...') : (isEditMode ? 'Update Project' : 'Add Project')}
          </button>
        </div>
      </div>

      {/* ── Map Modal ── */}
      {/* Ensure pac-container (Google autocomplete dropdown) renders above the fixed modal */}
      <style>{`.pac-container { z-index: 99999 !important; }`}</style>

      {mapVisible && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 9998, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div style={{ display: 'flex', flexDirection: 'column', width: 'min(800px, 100%)', height: 'min(600px, calc(100vh - 32px))', backgroundColor: '#fff', borderRadius: 14, overflow: 'hidden', boxShadow: '0 18px 48px rgba(0,0,0,0.28)' }}>
            {/* Map header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 22px', backgroundColor: '#fff', borderBottom: '1px solid #E5E7EB', flexShrink: 0 }}>
              <button type="button" onClick={() => setMapVisible(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 22, color: '#111827', padding: 0 }}>←</button>
              <span style={{ fontSize: 18, fontWeight: 700, color: '#1F2937' }}>Pick Location</span>
              <button type="button" onClick={confirmMapLocation}
                style={{ border: 'none', borderRadius: 6, padding: '6px 14px', backgroundColor: '#7357E8', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Done</button>
            </div>

            <div style={{ flex: 1, minHeight: 0, position: 'relative', display: 'flex', flexDirection: 'column', padding: 20 }}>
              <div style={{ flexShrink: 0, marginBottom: mapSearchResults.length > 0 ? 0 : 16, position: 'relative', zIndex: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', backgroundColor: '#fff', borderRadius: 24, border: '1px solid #D1D5DB', boxShadow: '0 2px 6px rgba(0,0,0,0.12)', padding: '4px 16px' }}>
                  <input type="text" placeholder="Search by project name or location..." value={mapSearch}
                    onChange={event => setMapSearch(event.target.value)}
                    style={{ flex: 1, padding: '10px 0', fontSize: 14, border: 'none', outline: 'none', background: 'transparent' }} />
                  {isMapSearching && (
                    <div style={{ width: 16, height: 16, border: '2px solid #E5E7EB', borderTopColor: '#2563EB', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                  )}
                </div>

                {mapSearchResults.length > 0 && (
                  <div style={{ marginTop: 4, backgroundColor: '#fff', borderRadius: 14, border: '1px solid #E5E7EB', boxShadow: '0 6px 18px rgba(0,0,0,0.15)', maxHeight: 220, overflowY: 'auto' }}>
                    {mapSearchResults.map((prediction, idx) => (
                      <div key={prediction.place_id || idx}
                        onClick={() => selectMapSearchResult(prediction)}
                        style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '12px 14px', fontSize: 13, color: '#374151', cursor: 'pointer', borderBottom: idx !== mapSearchResults.length - 1 ? '1px solid #F3F4F6' : 'none' }}
                        onMouseEnter={e => e.currentTarget.style.backgroundColor = '#F9FAFB'}
                        onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}>
                        <IoLocationSharp size={14} color="#2563EB" style={{ marginTop: 2, flexShrink: 0 }} />
                        <div>
                          <div style={{ fontWeight: 600, color: '#111827' }}>{prediction.structured_formatting?.main_text || prediction.description}</div>
                          {prediction.structured_formatting?.secondary_text && (
                            <div style={{ fontSize: 12, color: '#9CA3AF' }}>{prediction.structured_formatting.secondary_text}</div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

              <div style={{ flex: 1, minHeight: 0, marginTop: mapSearchResults.length > 0 ? 8 : 0 }}>
                {mapsLoaded ? (
                  <GoogleMap
                    mapContainerStyle={{ width: '100%', height: '100%' }}
                    center={mapCenter}
                    zoom={14}
                    onClick={event => setMarkerPos({ lat: event.latLng.lat(), lng: event.latLng.lng() })}
                    options={{ streetViewControl: true, mapTypeControl: true, fullscreenControl: true }}>
                    <GoogleMarker
                      position={markerPos}
                      draggable
                      onDragEnd={event => setMarkerPos({ lat: event.latLng.lat(), lng: event.latLng.lng() })}
                    />
                  </GoogleMap>
                ) : (
                  <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6B7280' }}>Loading map...</div>
                )}
              </div>

              <div style={{ position: 'absolute', bottom: 20, left: '50%', transform: 'translateX(-50%)', backgroundColor: 'rgba(0,0,0,0.7)', color: '#fff', fontSize: 12, padding: '8px 16px', borderRadius: 20, whiteSpace: 'nowrap' }}>
                Tap map or drag pin to select location
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Approval Modal ── */}
      {approvalModalVisible && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
          <div style={{ backgroundColor: '#fff', borderRadius: 20, padding: 28, width: '90%', maxWidth: 420, textAlign: 'center', boxShadow: '0 8px 32px rgba(0,0,0,0.15)' }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>✅</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#111827', marginBottom: 8 }}>
              {isEditMode ? 'Project Updated Successfully!' : 'Project Added Successfully!'}
            </div>
            <div style={{ fontSize: 14, color: '#6B7280', marginBottom: 24, lineHeight: 1.5 }}>
              Would you like to approve this project now or send it for verification?
            </div>

            {approvalLoading ? (
              <div style={{ padding: '20px 0', color: '#2563EB', fontSize: 14 }}>Processing...</div>
            ) : (
              <div>
                <button type="button" onClick={handleApproveNow}
                  style={{ width: '100%', padding: '14px', backgroundColor: '#10B981', color: '#fff', border: 'none', borderRadius: 12, fontSize: 15, fontWeight: 700, cursor: 'pointer', marginBottom: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                  ✔ Approve Now
                </button>
                <button type="button" onClick={handleSendToTask}
                  style={{ width: '100%', padding: '14px', backgroundColor: '#EFF6FF', color: '#2563EB', border: '1px solid #BFDBFE', borderRadius: 12, fontSize: 15, fontWeight: 600, cursor: 'pointer', marginBottom: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                  🕐 Send to Verification
                </button>
                <button type="button"
                  onClick={() => {
                    setApprovalModalVisible(false);
                    navigate('/AdminCrmManageProjects', { state: { userId, userName, userRole, userEmail, userPhone } });
                  }}
                  style={{ width: '100%', padding: '10px', backgroundColor: 'transparent', color: '#9CA3AF', border: 'none', borderRadius: 12, fontSize: 14, cursor: 'pointer' }}>
                  Skip for Now
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
